import React from "react";
import PropTypes from "prop-types";
import { Button, Caption1, Divider, Text, Title3 } from "@fluentui/react-components";
import { ArrowDownloadFilled } from "@fluentui/react-icons";
import bwipjs from "@bwip-js/browser";
import { Modal } from "../modal/Modal";
import { formatEventDateRange } from "../../utils/events-utils";
import { FormattedMessage } from "react-intl";
import getAuthUser from "../../utils/get-auth-user";

export const TicketDialog = ({ open = true, onClose, activation, userName, userEmail }) => {
  const address = activation?.address;
  const fullAddress =
    address &&
    [address.location_name, address.address, address.city, address.state, address.country, address.postcode]
      .filter(Boolean)
      .join(", ");

  const url = activation?.checkin_url;
  const qrCanvasRef = React.useRef(null);
  const [resolvedName, setResolvedName] = React.useState(userName || "");
  const [resolvedEmail, setResolvedEmail] = React.useState(userEmail || "");

  const generateQRCode = React.useCallback(() => {
    if (!qrCanvasRef.current || !url) return;
    bwipjs.toCanvas(qrCanvasRef.current, {
      bcid: "qrcode",
      text: url,
      scaleX: 2,
      scaleY: 2,
      includetext: false,
      textxalign: "center",
      paddingwidth: 3,
      paddingheight: 3,
      backgroundcolor: "#fff"
    });
  }, [url]);

  React.useEffect(() => {
    generateQRCode();
  }, [generateQRCode]);

  React.useEffect(() => {
    let mounted = true;
    const fetchUser = async () => {
      try {
        if (userName && userEmail) return;
        const store = window.APP?.store;
        const user = await getAuthUser(store);
        if (!mounted) return;
        setResolvedName(prev => prev || user?.name || "");
        setResolvedEmail(prev => prev || user?.email || "");
      } catch (_) {
        // ignore
      }
    };
    fetchUser();
    return () => {
      mounted = false;
    };
  }, [userName, userEmail]);

  const handleDownload = () => {
    const canvas = qrCanvasRef.current;
    if (!canvas) return;
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = `${activation?.slug || activation?.name || "ticket"}-qr.png`;
    link.click();
  };

  return (
    <Modal open={open} onOpenChange={onClose} onClose={onClose} title={activation?.name || "Ticket"}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "0 0 12px 0" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {activation && <Text weight="bold">{formatEventDateRange(activation)}</Text>}
          {fullAddress && <Text>{fullAddress}</Text>}
        </div>

        <Divider />

        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "12px 0" }}>
          <canvas
            ref={qrCanvasRef}
            width={288}
            height={288}
            style={{ borderRadius: 6, border: "1px solid #BDBDBD", background: "#fff" }}
            aria-label={"ticket-dialog.qr-aria-label"}
          />
        </div>

        <Divider />

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {(resolvedName || resolvedEmail) && (
            <>
              {resolvedName && <Title3 as="div">{resolvedName}</Title3>}
              {resolvedEmail && (
                <Text>
                  <Caption1>{resolvedEmail}</Caption1>
                </Text>
              )}
            </>
          )}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-start", marginTop: 6 }}>
            <Button
              style={{ width: "100%" }}
              appearance="primary"
              icon={<ArrowDownloadFilled />}
              onClick={handleDownload}
            >
              <FormattedMessage id="ticket-dialog.download" defaultMessage="Download" />
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

TicketDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  activation: PropTypes.object,
  userName: PropTypes.string,
  userEmail: PropTypes.string
};

export default TicketDialog;
